import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, MapPin, Bed, Plane, Store, ArrowRight, CalendarDays, Info, LogIn } from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import confetti from "canvas-confetti";
import winnersCupLogo from "@/assets/winners-cup-logo-red.png";

const navLinks = [
  { name: "About", href: "/event", icon: Info, description: "About the event" },
  { name: "Venue", href: "/venue", icon: MapPin, description: "Ontario Convention Center" },
  { name: "Hotel", href: "/hotel", icon: Bed, description: "Delta Hotels accommodation" },
  { name: "Travel", href: "/travel", icon: Plane, description: "Airport & transportation" },
  { name: "Vendors", href: "/vendors", icon: Store, description: "Exhibitors & partners" },
  { name: "2027", href: "/pre-register", icon: CalendarDays, description: "Save the Date — 2027" },
];

const Navbar = () => {
  const [scrollY, setScrollY] = useState(0);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [logoClicks, setLogoClicks] = useState(0);
  const [isSpinning, setIsSpinning] = useState(false);
  const location = useLocation();

  const isScrolled = scrollY > 50;
  const shrinkAmount = Math.min(scrollY / 100, 1);

  useEffect(() => {
    let ticking = false;
    const handleScroll = () => {
      if (!ticking) {
        requestAnimationFrame(() => { setScrollY(window.scrollY); ticking = false; });
        ticking = true;
      }
    };
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);

  useEffect(() => {
    document.body.style.overflow = isMobileMenuOpen ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [isMobileMenuOpen]);

  useEffect(() => {
    if (logoClicks > 0 && logoClicks < 7) {
      const t = setTimeout(() => setLogoClicks(0), 2000);
      return () => clearTimeout(t);
    }
  }, [logoClicks]);

  const handleLogoClick = useCallback((e: React.MouseEvent) => {
    const n = logoClicks + 1;
    setLogoClicks(n);
    if (n >= 7) {
      e.preventDefault();
      setIsSpinning(true);
      setLogoClicks(0);
      confetti({ particleCount: 100, spread: 70, origin: { y: 0.2 }, colors: ["#FF6B35", "#E8335A", "#FFD700", "#FF2D87"] });
      setTimeout(() => setIsSpinning(false), 1000);
    }
  }, [logoClicks]);

  const handleNavClick = () => {
    setIsMobileMenuOpen(false);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const logoHeight = 40 - (shrinkAmount * 12);
  const paddingY = 16 - (shrinkAmount * 6);

  return (
    <>
      <motion.nav
        initial={{ y: -100 }}
        animate={{ y: 0 }}
        transition={{ type: "spring", stiffness: 100, damping: 20 }}
        className="fixed top-0 left-0 right-0 z-50"
      >
        <motion.div
          className="absolute inset-0 border-b transition-all duration-500"
          style={{
            backgroundColor: isScrolled ? "hsl(0 0% 4% / 0.85)" : "transparent",
            backdropFilter: isScrolled ? "blur(20px) saturate(180%)" : "none",
            borderColor: isScrolled ? "hsl(0 0% 100% / 0.06)" : "transparent",
          }}
        />
        
        <div className="container mx-auto px-6 relative z-10 transition-all duration-300" style={{ paddingTop: paddingY, paddingBottom: paddingY }}>
          <div className="flex items-center justify-between">
            <Link to="/" onClick={(e) => { handleLogoClick(e); if (!isSpinning) handleNavClick(); }}>
              <motion.div 
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                animate={isSpinning ? { rotate: 720 } : { rotate: 0 }}
                transition={{ type: "spring", stiffness: 300, duration: isSpinning ? 1 : 0.3 }}
                className="relative flex items-center gap-2"
              >
                <img src={winnersCupLogo} alt="Winner's Cup" style={{ height: logoHeight, width: 'auto' }} className="transition-all duration-300" />
                <span className="font-display text-gradient text-lg" style={{ fontSize: `${1.1 - shrinkAmount * 0.2}rem` }}>2027</span>
                {logoClicks > 0 && logoClicks < 7 && (
                  <motion.span initial={{ scale: 0 }} animate={{ scale: 1 }} className="absolute -top-2 -right-2 bg-gradient-coral text-white text-xs w-5 h-5 rounded-full flex items-center justify-center font-bold">
                    {7 - logoClicks}
                  </motion.span>
                )}
              </motion.div>
            </Link>

            <div className="hidden lg:flex items-center gap-1">
              {navLinks.map((link, i) => (
                <motion.div key={link.name} initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}>
                  <Link
                    to={link.href}
                    onClick={handleNavClick}
                    className={`px-3 py-2 rounded-lg font-medium text-sm transition-all ${
                      location.pathname === link.href
                        ? "text-primary bg-primary/10"
                        : "text-muted-foreground hover:text-foreground hover:bg-muted/60"
                    }`}
                  >
                    {link.name}
                  </Link>
                </motion.div>
              ))}
            </div>

            <motion.button
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
              className="lg:hidden p-2 rounded-lg text-foreground hover:bg-muted/60"
              whileTap={{ scale: 0.9 }}
              aria-label={isMobileMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMobileMenuOpen}
            >
              <AnimatePresence mode="wait">
                {isMobileMenuOpen ? (
                  <motion.div key="close" initial={{ rotate: -90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: 90, opacity: 0 }}><X size={24} /></motion.div>
                ) : (
                  <motion.div key="menu" initial={{ rotate: 90, opacity: 0 }} animate={{ rotate: 0, opacity: 1 }} exit={{ rotate: -90, opacity: 0 }}><Menu size={24} /></motion.div>
                )}
              </AnimatePresence>
            </motion.button>
          </div>
        </div>
      </motion.nav>

      <AnimatePresence>
        {isMobileMenuOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-40 lg:hidden bg-secondary"
          >
            <div className="absolute inset-0 overflow-hidden pointer-events-none">
              <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full" style={{ background: "radial-gradient(circle, hsl(15 100% 55% / 0.15) 0%, transparent 70%)" }} />
              <div className="absolute -bottom-20 -left-20 w-48 h-48 rounded-full" style={{ background: "radial-gradient(circle, hsl(340 90% 55% / 0.1) 0%, transparent 70%)" }} />
            </div>

            <div className="relative z-10 h-full flex flex-col pt-24 pb-8 px-6 overflow-y-auto">
              <nav className="flex-1 flex flex-col gap-2">
                {navLinks.map((link, i) => {
                  const Icon = link.icon;
                  const isActive = location.pathname === link.href;
                  return (
                    <motion.div key={link.name} initial={{ opacity: 0, x: -30 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: i * 0.06 }}>
                      <Link to={link.href} onClick={handleNavClick}
                        className={`flex items-center gap-4 p-4 rounded-2xl transition-all ${isActive ? "bg-gradient-coral" : "bg-muted/60 border border-border/60 hover:bg-white/8"}`}
                      >
                        <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${isActive ? "bg-muted" : "bg-primary/10"}`}>
                          <Icon className={`w-5 h-5 ${isActive ? "text-foreground" : "text-primary"}`} />
                        </div>
                        <div className="flex-1">
                          <span className="font-display text-lg text-foreground block">{link.name}</span>
                          <span className="text-muted-foreground text-xs">{link.description}</span>
                        </div>
                        <ArrowRight className={`w-4 h-4 ${isActive ? "text-foreground" : "text-muted-foreground"}`} />
                      </Link>
                    </motion.div>
                  );
                })}
              </nav>
              <p className="text-center text-muted-foreground text-sm mt-8">Winner's Cup 2027 • Ontario, CA</p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default Navbar;
